import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import Portal from 'react-portal'

import styles from './Tooltip.css'

const CURSOR_SIZE = 16
const COMMON_OFFSET = 16
const TOOLTIP_SHOW_DELAY = 500

class TooltipManager {
    constructor() {
        this.tooltips = new Map()
        this.activeTooltip = null

        this.handleMouseOver = this.handleMouseOver.bind(this)
        this.handleMouseMove = this.handleMouseMove.bind(this)
        this.handleMouseLeave = this.handleMouseLeave.bind(this)
        this.handleMouseDown = this.handleMouseDown.bind(this)
    }

    updateActiveTooltip() {
        if (!this.activeTooltip)
            return
        const target = this.activeTooltip.target
        const tooltip = this.tooltips.get(target.dataset.for)
        if (tooltip !== this.activeTooltip)
            this.activateTooltip(tooltip, target)
    }

    addTooltip(id, tooltip) {
        if (this.tooltips.has(id)) {
            // eslint-disable-next-line no-console
            console.error(`Duplicate tooltip id - "${id}"`)
            return
        }
        if (this.tooltips.size === 0)
            document.addEventListener('mouseover', this.handleMouseOver)
        this.tooltips.set(id, tooltip)
    }

    removeTooltip(id) {
        const tooltip = this.tooltips.get(id)
        if (!tooltip) {
            // eslint-disable-next-line no-console
            console.error(`Try delete tooltip id "${id}" that already deleted`)
            return
        }
        if (tooltip === this.activeTooltip)
            this.destroyActiveTooltip()
        this.tooltips.delete(id)
        if (this.tooltips.size === 0)
            document.removeEventListener('mouseover', this.handleMouseOver)
    }

    handleMouseOver(e) {
        let target = e.target
        while (!target.dataset.tip) {
            target = target.parentNode
            if (target === e.currentTarget)
                return
        }
        if (target.dataset.tip) {
            const id = target.dataset.for
            const tooltip = this.tooltips.get(id)
            if (tooltip && this.activeTooltip !== tooltip) {
                tooltip.setClientPosition(e.clientX, e.clientY)
                this.activateTooltip(tooltip, target)
            }
        }
    }

    activateTooltip(tooltip, target) {
        if (this.activeTooltip)
            this.destroyActiveTooltip()
        this.activeTooltip = tooltip
        this.activeTooltip.target = target
        this.activeTooltip.activate()
        target.addEventListener('mousemove', this.handleMouseMove, true)
        target.addEventListener('mouseleave', this.handleMouseLeave)
        target.addEventListener('mousedown', this.handleMouseDown)
    }

    handleMouseMove(e) {
        this.activeTooltip && this.activeTooltip.move(e)
    }

    handleMouseLeave() {
        this.destroyActiveTooltip()
    }

    handleMouseDown() {
        this.destroyActiveTooltip()
    }

    destroyActiveTooltip() {
        this.activeTooltip.hide()
        this.activeTooltip.target.removeEventListener('mousemove', this.handleMouseMove, true)
        this.activeTooltip.target.removeEventListener('mouseleave', this.handleMouseLeave)
        this.activeTooltip.target.removeEventListener('mousedown', this.handleMouseDown)
        this.activeTooltip.target = null
        this.activeTooltip = null
    }
}


/**
 * @example ./Tooltip.components.md
 */

class Tooltip extends React.Component {
    static propTypes = {
        children: PropTypes.node,
        id: PropTypes.string.isRequired,
        bounds: PropTypes.shape({
            top: PropTypes.number,
            right: PropTypes.number,
            bottom: PropTypes.number,
            left: PropTypes.number,
        }),
        isDemo: PropTypes.bool,
        isWide: PropTypes.bool,
        getContent: PropTypes.func,
    }

    static defaultProps = {
        getContent: () => {},
    }

    static manager = new TooltipManager()

    constructor(props) {
        super(props)

        this.state = {
            initialized: this.props.isDemo,
            visible: false,
            top: 0,
            left: 0,
            clientX: 0,
            clientY: 0,
        }
        this.target = null

        this.show = this.show.bind(this)
    }

    componentDidMount() {
        Tooltip.manager.addTooltip(this.props.id, this)
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.id !== this.props.id) {
            Tooltip.manager.addTooltip(nextProps.id, this)
            Tooltip.manager.removeTooltip(this.props.id)
        }
    }

    componentWillUnmount() {
        Tooltip.manager.removeTooltip(this.props.id)
    }

    activate() {
        if (!this.state.initialized) {
            this.setState({ initialized: true }, () => {
                this.timeoutId = setTimeout(this.show, TOOLTIP_SHOW_DELAY)
            })
        } else {
            this.timeoutId = setTimeout(this.show, TOOLTIP_SHOW_DELAY)
        }
    }

    setClientPosition(clientX, clientY) {
        this.setState({ clientX, clientY })
    }

    move({ clientX, clientY }) {
        this.setClientPosition(clientX, clientY)
    }

    show() {
        const position = this.getPosition()
        this.setState({
            ...position,
            visible: true,
        })
    }

    hide() {
        this.clearTimeouts()
        this.setState({ visible: false })
    }

    clearTimeouts() {
        this.timeoutId && clearTimeout(this.timeoutId)
    }

    getPosition() {
        const { clientX, clientY } = this.state

        const tooltipHeight = this._tooltip.offsetHeight
        const tooltipWidth = this._tooltip.offsetWidth

        const { clientHeight, clientWidth } = document.documentElement

        const bounds = {
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            ...this.props.bounds,
        }

        const topLimit = bounds.top
        const rightLimit = clientWidth - bounds.right
        const bottomLimit = clientHeight - bounds.bottom
        const leftLimit = bounds.left

        const fitsTop = (tooltipTop) => tooltipTop >= topLimit
        const fitsRight = (tooltipLeft) => tooltipLeft + tooltipWidth <= rightLimit
        const fitsBottom = (tooltipTop) => tooltipTop + tooltipHeight <= bottomLimit
        const fitsLeft = (tooltipLeft) => tooltipLeft >= leftLimit

        let tooltipTop = clientY + window.scrollY
        let tooltipLeft = clientX + CURSOR_SIZE - COMMON_OFFSET

        const windowScrollY = window.scrollY || window.pageYOffset

        if (!fitsRight(tooltipLeft)) {
            tooltipLeft = clientX - tooltipWidth
        }

        if (!fitsLeft(tooltipLeft)) {
            tooltipLeft = leftLimit
        }

        if (!fitsTop(tooltipTop)) {
            tooltipTop = clientY + windowScrollY + CURSOR_SIZE - COMMON_OFFSET

            if (!fitsRight(tooltipLeft)) {
                tooltipTop = clientY + windowScrollY - COMMON_OFFSET
            }
        }

        if (!fitsBottom(tooltipTop)) {
            tooltipTop = bottomLimit - tooltipHeight
        }

        return { top: tooltipTop, left: tooltipLeft }
    }

    render() {
        if (!this.state.initialized) return null

        const classNameTooltip = classNames(styles.base, {
            [styles.isVisible]: this.state.visible,
            [styles.isDemo]: this.props.isDemo,
            [styles.isWide]: this.props.isWide,
        })

        const inlineStyles = {
            top: this.state.visible ? this.state.top : 0,
            left: this.state.visible ? this.state.left : 0,
        }

        const content = this.props.children ? this.props.children : (
            this.target && this.props.getContent(this.target.dataset.tip)
        )

        const tooltip = (
            <div
                className={classNameTooltip}
                style={inlineStyles}
                ref={(c) => this._tooltip = c}
            >
                <div className={styles.body}>
                    <div className={styles.wrapper} />
                    <div className={styles.content}>
                        {content}
                    </div>
                </div>
            </div>
        )

        return this.props.isDemo ? tooltip : <Portal isOpened>{tooltip}</Portal>
    }
}

export default Tooltip



// WEBPACK FOOTER //
// ../node_modules/react-mt-bootstrap/lib/components/Tooltip/Tooltip.jsx